import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth, defaultRoute } from "../context/AuthContext";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function Shell({ title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid={testid}>
      <div className="w-full max-w-md apha-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-md bg-[#1E3A8A] text-white flex items-center justify-center"><KeyRound className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-semibold leading-tight">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

function NewPasswordFields({ form, set, t, withCurrent }) {
  return (
    <>
      {withCurrent && (
        <div className="space-y-1.5"><Label>{t("currentPassword")}</Label>
          <Input type="password" autoComplete="current-password" data-testid="current-password-input" value={form.current} onChange={set("current")} required /></div>
      )}
      <div className="space-y-1.5"><Label>{t("newPassword")} <span className="text-muted-foreground">({t("passwordMinHint")})</span></Label>
        <Input type="password" autoComplete="new-password" data-testid="new-password-input" value={form.password} onChange={set("password")} minLength={8} required /></div>
      <div className="space-y-1.5"><Label>{t("confirmPassword")}</Label>
        <Input type="password" autoComplete="new-password" data-testid="confirm-password-input" value={form.confirm} onChange={set("confirm")} required /></div>
    </>
  );
}

export function SetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(undefined);
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  useEffect(() => {
    api.post("/auth/password-token/verify", { token }).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error(t("passwordMismatch"));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, new_password: form.password });
      applySession(data.token, data.user);
      toast.success(t("passwordChanged"));
      navigate(defaultRoute(data.user.role), { replace: true });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  if (info === undefined) return <Shell title={t("setPassword")} testid="set-password-page"><p className="text-sm text-muted-foreground">…</p></Shell>;
  if (info === null) {
    return (
      <Shell title={t("setPassword")} testid="set-password-page">
        <p className="text-sm text-[#991B1B]" data-testid="set-password-invalid">{t("passwordLinkInvalid")}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/login")} data-testid="set-password-go-login">{t("signIn")}</Button>
      </Shell>
    );
  }
  return (
    <Shell title={t("setPassword")} subtitle={`${info.display_name} · ${info.email}`} testid="set-password-page">
      <form className="space-y-4" onSubmit={submit}>
        <NewPasswordFields form={form} set={set} t={t} />
        <Button type="submit" data-testid="set-password-submit" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy || form.password.length < 8}>{busy ? "..." : t("setPasswordAndSignIn")}</Button>
      </form>
      <p className="mt-5 flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" />{t("passwordLinkOnce")}</p>
    </Shell>
  );
}

export function ForcePasswordChange() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { user, applySession, logout } = useAuth();
  const [form, setForm] = useState({ current: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error(t("passwordMismatch"));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.password });
      applySession(data.token, { must_change_password: false });
      toast.success(t("passwordChanged"));
      navigate(defaultRoute(user.role), { replace: true });
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("passwordChangeRequired")} subtitle={t("passwordChangeRequiredHint")} testid="force-password-page">
      <form className="space-y-4" onSubmit={submit}>
        <NewPasswordFields form={form} set={set} t={t} withCurrent />
        <Button type="submit" data-testid="force-password-submit" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy || form.password.length < 8}>{busy ? "..." : t("save")}</Button>
      </form>
      <button type="button" className="mt-4 text-xs text-muted-foreground hover:underline" data-testid="force-password-logout" onClick={() => { logout(); navigate("/login"); }}>{t("logout")}</button>
    </Shell>
  );
}
